/** @jsxImportSource react */
import { useState } from 'react'
import { Box, Button, Chip, Link, Stack, Typography } from '@mui/material'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import AddIcon from '@mui/icons-material/Add'
import { useQuery } from '@tanstack/react-query'
import { meetingsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorMessage } from '../../utils/apiErrors'
import MeetingDialog from './MeetingDialog'
import { RESULT_LABEL, STATUS_LABEL, whenLabel } from './meetingModel'

const SHOWN_PAST = 3

/** One meeting or call as a compact row. */
export const MeetingRow = ({ m, onOpen }) => {
  const Icon = m.kind === 'CALL' ? PhoneOutlinedIcon : EventOutlinedIcon
  const detail = m.kind === 'CALL' ? [m.callDirection === 'INCOMING' ? 'Incoming' : 'Outgoing', RESULT_LABEL[m.callResult]].filter(Boolean).join(' · ')
    : [m.location, m.guestEmails.length ? `${m.guestEmails.length} guest${m.guestEmails.length === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')
  return (
    <Box onClick={() => onOpen(m)} sx={{ display: 'flex', gap: 1.25, py: 0.75, px: 0.5, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
      <Icon fontSize="small" color={m.status === 'PLANNED' ? 'primary' : 'action'} sx={{ mt: 0.25 }} />
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, textDecoration: m.status === 'CANCELLED' ? 'line-through' : 'none', wordBreak: 'break-word' }}>{m.title}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {whenLabel(m.startsAt, m.endsAt)}{detail ? ` · ${detail}` : ''}
        </Typography>
      </Box>
      {m.status !== 'PLANNED' && m.kind === 'MEETING' && <Chip size="small" variant="outlined" label={STATUS_LABEL[m.status]} />}
    </Box>
  )
}

/** A record's meetings and calls: coming up first, then the latest few past ones. */
const RecordMeetings = ({ record }) => {
  const tenantId = useTenantKey()
  const [dialog, setDialog] = useState(null)   // null | { kind } | meeting
  const [showAll, setShowAll] = useState(false)
  const { data = [], isLoading, error } = useQuery({
    queryKey: [tenantId, 'meetings', 'record', record.id],
    queryFn: () => meetingsAPI.ofRecord(record.id),
  })
  const now = new Date()
  const upcoming = data.filter((m) => m.status === 'PLANNED' && new Date(m.endsAt) >= now).reverse()
  const past = data.filter((m) => !upcoming.includes(m))

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Meetings & calls{upcoming.length > 0 && <Typography component="span" color="text.secondary"> · {upcoming.length} coming up</Typography>}</Typography>
        <Button size="small" startIcon={<PhoneOutlinedIcon />} onClick={() => setDialog({ kind: 'CALL' })}>Log call</Button>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setDialog({ kind: 'MEETING' })}>Meeting</Button>
      </Box>
      {isLoading && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
      {error && <Typography variant="body2" color="error">{getErrorMessage(error)}</Typography>}
      {!isLoading && !error && data.length === 0 && (
        <Typography variant="body2" color="text.secondary">No meetings or calls yet.</Typography>
      )}
      <Stack>
        {upcoming.map((m) => <MeetingRow key={m.id} m={m} onOpen={setDialog} />)}
        {(showAll ? past : past.slice(0, SHOWN_PAST)).map((m) => <MeetingRow key={m.id} m={m} onOpen={setDialog} />)}
      </Stack>
      {past.length > SHOWN_PAST && (
        <Link component="button" type="button" variant="caption" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show fewer' : `Show all ${past.length} past`}
        </Link>
      )}
      {dialog && (
        <MeetingDialog open onClose={() => setDialog(null)} meeting={dialog.id ? dialog : null} kind={dialog.kind}
          recordRef={dialog.id ? null : { id: record.id, title: record.title }} />
      )}
    </Box>
  )
}

export default RecordMeetings
